import React from 'react';

/**
 * Single source of truth for the onboarding step footer:
 * — progress dots (44pt tap targets, decorative wrapper)
 * — Back button (when not on first step)
 * — primary CTA (label switches to "Get started" on the last form step)
 *
 * Replaces the four near-identical inline footer blocks that previously lived
 * in Onboarding.js (multiple-choice first-step, multiple-choice non-first,
 * form-step, completion).
 */
const OnboardingFooter = ({
  visibleSteps,
  currentStep,
  setCurrentStep,
  canProceed,
  onBack,
  onNext,
  primaryLabel = 'Continue',
  showBack = true,
  showDots = true,
}) => {
  return (
    <div className="flex flex-col items-center mt-8">
      {showDots && visibleSteps && visibleSteps.length > 1 && (
        <div className="flex justify-center items-center gap-1 mb-4">
          {visibleSteps.map((step, index) => (
            <button
              key={step.id || index}
              onClick={() => index <= currentStep && setCurrentStep && setCurrentStep(index)}
              disabled={index > currentStep}
              aria-label={`Go to step ${index + 1}`}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <span
                className={`block transition-all duration-200 rounded-full ${
                  index === currentStep
                    ? 'w-2.5 h-2.5 bg-blue-500'
                    : index < currentStep
                    ? 'w-2 h-2 bg-blue-400/60'
                    : 'w-2 h-2 bg-gray-600'
                }`}
              />
            </button>
          ))}
        </div>
      )}

      <div className="flex justify-center items-center gap-2 w-full max-w-md">
        {showBack && currentStep > 0 && (
          <button
            onClick={onBack}
            className="flex-1 bg-gray-700 hover:bg-gray-600 active:bg-gray-700/90 text-white font-semibold py-3 rounded-lg transition-colors min-h-[44px] active:scale-[0.98]"
          >
            Back
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`flex-1 font-semibold py-3 rounded-lg transition-all duration-200 shadow-lg min-h-[44px] active:scale-[0.98] ${
            canProceed
              ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-500/20'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
};

export default OnboardingFooter;
